import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import { IRate, IRateAPI, ICarrier } from 'types/contractLanes'
import { selectLaneInfo } from 'apps/marketplace/store/laneMetrics'
import { ISpecificLane } from 'apps/marketplace/store/routingGuide/types'
import { useCarrier } from 'apps/marketplace/utils/helpers/useCarrier'
import { useRateData } from 'apps/marketplace/utils/helpers/useRateData'
import { isRecurringRateValid } from 'apps/marketplace/utils/helpers/validation'
import { FormModal } from 'transfix-ui'
import {
  Capacity,
  Carrier,
  DatOption,
  DeliveryPreference,
  Dispatcher,
  Equipment,
  ExpirationDate,
  LoadType,
  Locations,
  PickupPreference,
  Rate,
  RateType,
  RadiusRates,
  Shipper,
  StartDate,
} from './components'

interface IEditLaneModalProps {
  dialogOpen: boolean
  dialogData?: Partial<IRate>
  errors?: string
  addRecurringRate: (data: IRateAPI, specificLaneId?: string) => void
  editRecurringRate: (data: IRateAPI, specificLaneId?: string) => void
  closeLaneSidebarDialog: () => void
  specificLaneId?: string
  specificLane?: ISpecificLane
  carrier?: Partial<ICarrier>
  creationSource: string
  pickupRadiusMiles?: number | null
  deliveryRadiusMiles?: number | null
}

export const EditLaneModal = memo<IEditLaneModalProps>(
  ({
    addRecurringRate,
    carrier,
    closeLaneSidebarDialog,
    creationSource,
    deliveryRadiusMiles,
    dialogData,
    dialogOpen,
    editRecurringRate,
    errors,
    pickupRadiusMiles,
    specificLane,
    specificLaneId,
  }) => {
    const laneInfo = useSelector(selectLaneInfo)
    const [rateData, changeRateData] = useRateData({
      dialogOpen,
      existingRate: dialogData,
      laneInfo,
      errors,
      carrier,
      specificLane,
      pickupRadiusMiles,
      deliveryRadiusMiles,
    })
    useCarrier(rateData, changeRateData, dialogOpen)
    const isFromManageLanes = creationSource === 'manage_lanes'

    const submitForm = () => {
      changeRateData({ buttonClicked: true })
      if (dialogData?.contractId && rateData) {
        editRecurringRate(rateData, specificLaneId)
      } else {
        addRecurringRate(rateData, specificLaneId)
      }
    }

    const { destination, origin } = laneInfo || {}
    const header = dialogData?.contractId ? 'Edit Lane Rate' : 'Add Carrier to Routing Guide'
    const disableButton = !isRecurringRateValid(rateData)
    const { rateType } = rateData || {}

    const isCarrierPreloaded = Boolean(carrier)

    return (
      <FormModal
        title={header}
        size={6}
        onClose={closeLaneSidebarDialog}
        open={dialogOpen}
        error={errors}
        actions={[
          {
            type: 'dismiss',
            label: 'Cancel',
            dataTest: 'cancel-rate-button',
          },
          {
            type: 'confirm',
            label: header,
            disabled: disableButton,
            action: submitForm,
            dataTest: 'save-button',
          },
        ]}>
        <div className='row'>
          <div className='col-xs-12'>
            <Carrier
              carrierId={rateData.carrierId}
              carrierName={rateData.carrierName}
              contractId={rateData.contractId}
              readOnly={isCarrierPreloaded}
              onChange={changeRateData}
            />
            <RateType
              rateType={rateType}
              hide={!!(rateData.contractId || !rateData.laneSupportsBandedRates || carrier)}
              onChange={changeRateData}
            />
            {rateType && (
              <>
                <Locations
                  rateType={rateType}
                  pickupLocation={rateData.pickupLocation}
                  pickupRadiusMiles={rateData.pickupRadiusMiles}
                  deliveryLocation={rateData.deliveryLocation}
                  deliveryRadiusMiles={rateData.deliveryRadiusMiles}
                  onChange={changeRateData}
                  disabled={isFromManageLanes}
                />
                <RadiusRates
                  rateType={rateType}
                  radiusRates={rateData.radiusRates}
                  onChange={changeRateData}
                />
                <div className='row'>
                  <Rate rateType={rateType} rate={rateData.rate} onChange={changeRateData} />
                  <Equipment
                    equipmentId={rateData.equipmentId}
                    isOnlyBeer={rateData.isOnlyBeer}
                    isOnlyDropTrailer={rateData.isOnlyDropTrailer}
                    onChange={changeRateData}
                    disabled={isFromManageLanes}
                  />
                </div>
                {!carrier && (
                  <DatOption
                    isDatMarketApplicable={rateData.isDatMarketApplicable}
                    origin={origin}
                    destination={destination}
                    onChange={changeRateData}
                  />
                )}
                <LoadType loadType={rateData.loadType} onChange={changeRateData} />
                <ExpirationDate
                  expirationType={rateData.expirationType}
                  expirationDate={rateData.expirationDate}
                  onChange={changeRateData}
                />
                <StartDate
                  startType={rateData.startType}
                  startDate={rateData.startDate}
                  onChange={changeRateData}
                />
                <Capacity
                  carrierId={String(rateData.carrierId)}
                  carrierCapacity={rateData.carrierCapacity}
                  pickupLocation={rateData.pickupLocation}
                  onChange={changeRateData}
                  displayLink={!isCarrierPreloaded}
                />
                <Dispatcher
                  dispatcher={rateData.dispatcher}
                  dispatcherName={rateData.dispatcherName}
                  displayUserCreation={rateData.displayUserCreation}
                  carrierApplicationId={rateData.carrierId}
                  user={rateData.user}
                  onChange={changeRateData}
                />
                <PickupPreference
                  pickupTimeType={rateData.pickupTimeType}
                  pickupTimeEveryday={rateData.pickupTimeEveryday}
                  pickupTimePreferences={rateData.pickupTimePreferences}
                  carrierCapacity={rateData.carrierCapacity}
                  onChange={changeRateData}
                />
                <DeliveryPreference
                  deliveryTimePreference={rateData.deliveryTimePreference}
                  onChange={changeRateData}
                />
                <Shipper
                  shipperId={rateData.shipperId}
                  shipperName={rateData.shipperName}
                  isShipperExclusive={rateData.isShipperExclusive}
                  onChange={changeRateData}
                  disabled={isFromManageLanes}
                />
              </>
            )}
          </div>
        </div>
      </FormModal>
    )
  }
)
